import cx from "classnames";
import { Fragment } from "react";
import { Flex, Text, Button, List, FieldTextGroup } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { GetFeedbackAlert, enumRuleSets, GetDefinitionData } from "../RowLevelSecurityModal.utils";
import EditableItemGroup from "@/appComponents/Blocks/Utils/EditableItemGroup/EditableItemGroup";
import { GetRulePropertyString } from "../../SecurityUtils";
import { JSX } from "react/jsx-runtime";

interface RuleCriteriaSectionProps {
  selectedRule: string;
  selectedConditionalRule: string;
  currentConditionHasRules: boolean;
  currentRuleHasFields?: boolean;
  conditionalRules: Record<string, Record<string, any>>;
  openDefinitionEditor: () => void;
  handleDefinitionUpdate: (action: string, index: string) => void;
}

export const RuleCriteriaSection = ({
  selectedRule,
  selectedConditionalRule,
  currentConditionHasRules,
  currentRuleHasFields,
  conditionalRules,
  openDefinitionEditor,
  handleDefinitionUpdate
}: RuleCriteriaSectionProps) => {
  const BuildCurrentDefinitions = () => {
    let definitions: { value: string; content: JSX.Element; divider: boolean }[] = [];
    if (!conditionalRules[selectedConditionalRule]) return definitions;
    const conditionalRule = conditionalRules[selectedConditionalRule];
    const ruleDefinitions: any[] = Array.isArray(conditionalRule["rules"])
      ? conditionalRule["rules"]
      : [];

    for (let i = 0; i <= ruleDefinitions.length - 1; i++) {
      const theRule = ruleDefinitions[i];
      const theProperty = theRule["properties"];
      const theDefinitionObj = theRule["definition"];
      const thePositionDefinitionObj = theRule["pos_definition"] ?? null;
      const definitionData = GetDefinitionData(theDefinitionObj);
      if (thePositionDefinitionObj) {
        const posDefinitionData = GetDefinitionData(thePositionDefinitionObj);
        definitionData.push(...posDefinitionData);
      }

      const condRuleOperator = theRule["condRuleOperator"] === 1 ? "AND" : "OR";
      const { propertyString } = GetRulePropertyString(theProperty);
      const option = {
        value: i.toString(),
        content: (
          <EditableItemGroup
            centerActions={false}
            customStyle={{ padding: "4px" }}
            spaceBetween
            actions={[
              {
                iconName: "pen",
                tooltipProps: { message: appstr("Edit rule criteria") },
                onClick: () => handleDefinitionUpdate("edit", i.toString()),
                dataTestId: `account-settings-security-rls-criteria-${i}-edit-btn`
              },
              {
                iconName: "copy",
                tooltipProps: { message: appstr("Duplicate rule criteria") },
                onClick: () => handleDefinitionUpdate("copy", i.toString()),
                dataTestId: `account-settings-security-rls-criteria-${i}-copy-btn`
              },
              {
                iconName: "trash-can",
                iconProps: { color: "error" },
                tooltipProps: { message: appstr("Remove rule criteria") },
                onClick: () => handleDefinitionUpdate("remove", i.toString()),
                dataTestId: `account-settings-security-rls-criteria-${i}-delete-btn`
              }
            ]}
            buttonsInHierarchy
          >
            <Flex direction={"column"} gap={"8px"}>
              <Text size={"small"} modifier={"bold"}>
                {appstr("Rule criteria")}
              </Text>
              <FieldTextGroup
                columnGap={20}
                columns={1}
                data={[
                  { field: appstr("Rule comparison"), value: condRuleOperator },
                  { field: appstr("Value comparison"), value: propertyString }
                ]}
              />
              <FieldTextGroup columnGap={20} columns={1} data={definitionData} />
            </Flex>
          </EditableItemGroup>
        ),
        divider: i < ruleDefinitions.length - 1
      };
      definitions.push(option);
    }

    return definitions;
  };

  const currentDefinitions = BuildCurrentDefinitions();

  return (
    <Flex direction={"column"} gap={"4px"} width={"100%"} height={"100%"}>
      <Flex justifyContent={"space-between"} gap={"4px"} alignItems={"center"} width={"100%"}>
        <Text modifier={"bold"} size={"medium"}>
          {appstr("Conditions")}
        </Text>
        <Flex className={"oc-right-justify"} padding={"0 4px 0 0"}>
          <Button
            size={"medium"}
            disabled={selectedRule !== enumRuleSets.conditional}
            className={cx("oc-special-secondary", "oc-special-secondary--fit")}
            modifier={"secondary"}
            iconProps={{ name: "plus" }}
            onClick={() => openDefinitionEditor()}
            dataTestId="rls-modal-add-rule-criteria"
          >
            {appstr("Rule criteria")}
          </Button>
        </Flex>
      </Flex>
      <div
        className={cx(
          "oc-list-container",
          "ocn-list-scrollbar",
          "oc-list-container--divider-right"
        )}
      >
        {selectedRule === enumRuleSets.conditional && currentConditionHasRules && (
          <Fragment>
            {GetFeedbackAlert(appstr("rowsecconditional"))}
            <List
              enableVirtualization={false}
              coloredSelection={true}
              showCheck={false}
              value={0}
              optionSize={"small"}
              options={currentDefinitions}
              listWidth="fill"
            />
          </Fragment>
        )}
        {selectedRule === enumRuleSets.conditional && !currentConditionHasRules && (
          <Button
            modifier="underline"
            size="medium"
            type="button"
            underline
            onClick={() => openDefinitionEditor()}
            dataTestId="account-settings-security-rls-no-conditions-add-btn"
          >
            {appstr("No rule conditions")}
          </Button>
        )}
        {selectedRule === enumRuleSets.allowed &&
          GetFeedbackAlert(
            currentRuleHasFields
              ? appstr("rowsecallowedfields")
              : appstr("rowsecallowedfieldsimplicit")
          )}
        {selectedRule === enumRuleSets.restricted &&
          GetFeedbackAlert(appstr("rowsecrestrictedfields"))}
      </div>
    </Flex>
  );
};
